import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon } from 'lucide-react';
import { getSalePricing, useTranslation } from '@dukanti/shared';
import { formatPrice } from '@dukanti/shared';
import type { ProductWithImages } from '@dukanti/shared';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ProductActionsMenu } from './product-actions-menu';

export interface ProductCardProps {
  readonly product: ProductWithImages;
  /** Called when the user picks "Delete" from the kebab menu. The
   *  parent (products-list-route) owns the confirmation dialog lifted
   *  from F4c-5. */
  readonly onRequestDelete?: (product: ProductWithImages) => void;
  /** F-028: opens the publish dialog for this product. */
  readonly onRequestPublish?: (product: ProductWithImages) => void;
  /** F-028: opens the unpublish dialog for this product. */
  readonly onRequestUnpublish?: (product: ProductWithImages) => void;
  /** F-028: parent store's `published` flag. Drives the Publish menu
   *  item's disabled state alongside `inventory === 0`. */
  readonly storePublished?: boolean;
}

/**
 * Desktop/tablet product card for the admin products list.
 *
 * Interaction model (per F-003 design D5):
 *   - Tapping the card body navigates to the product detail page.
 *   - The kebab menu is a desktop-only affordance (revealed on hover
 *     via Tailwind's group-hover); on mobile, users tap the card to
 *     reach detail and use the detail page for Edit/Delete.
 *   - Edit navigates to `/products/:id/edit`.
 *   - Delete invokes `onRequestDelete(product)` so the parent can open
 *     the delete-confirm dialog that lands with F4c-5.
 *
 * Everything text-bearing is wrapped in `dir={dir}` via the parent
 * route; card internals use logical Tailwind spacing so they flow
 * correctly in both LTR and RTL.
 */
export function ProductCard({
  product,
  onRequestDelete,
  onRequestPublish,
  onRequestUnpublish,
  storePublished,
}: ProductCardProps) {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();

  function goToDetail() {
    navigate(`/products/${product.id}`);
  }

  function onCardKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToDetail();
    }
  }

  const firstImage = product.images[0];
  const isDraft = !product.published;
  const availabilityKey =
    product.inventory > 0
      ? 'admin.products.table.availability.available'
      : 'admin.products.table.availability.outOfStock';
  const availabilityClass = product.inventory <= 0 ? 'text-destructive' : 'text-muted-foreground';
  const salePricing = getSalePricing(product);
  const effectivePrice = formatPrice(salePricing.effectivePriceCents, lang);
  const regularPrice = formatPrice(salePricing.regularPriceCents, lang);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={onCardKeyDown}
      data-testid={`product-card-${product.id}`}
      className={cn(
        'group relative cursor-pointer overflow-hidden',
        'bg-card border-border shadow-admin-card rounded-lg border',
        'transition-[box-shadow,transform] duration-200 ease-out',
        'hover:shadow-admin-elevated hover:-translate-y-px',
        'focus-visible:ring-zaatar focus-visible:ring-offset-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      )}
    >
      {/* Image area */}
      <div className="relative h-44 overflow-hidden">
        {firstImage ? (
          <img
            src={firstImage.url}
            alt={product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="bg-admin-surface-muted flex h-full w-full items-center justify-center"
            aria-hidden="true"
            data-testid={`product-card-${product.id}-image-placeholder`}
          >
            <ImageIcon className="text-muted-foreground h-12 w-12 opacity-60" strokeWidth={1.5} />
          </div>
        )}

        {isDraft ? (
          <Badge
            variant="neutral"
            className="absolute start-2.5 top-2.5 rounded px-2 py-0.5 text-[11px] uppercase tracking-wider"
            data-testid={`product-card-${product.id}-draft-badge`}
          >
            {t('admin.products.badge.draft')}
          </Badge>
        ) : null}

        {salePricing.isOnSale ? (
          <div className="absolute end-2.5 top-2.5 flex flex-col items-end gap-1">
            <Badge variant="warning" data-testid={`product-card-${product.id}-sale-badge`}>
              {t('admin.products.badge.onSale')}
            </Badge>
            {salePricing.discountPercent !== null ? (
              <Badge variant="default" data-testid={`product-card-${product.id}-discount-badge`}>
                {t('admin.products.badge.discountPercent', {
                  percent: salePricing.discountPercent,
                })}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className="p-4">
        <div
          className="font-display text-foreground mb-2 line-clamp-2 text-base leading-snug"
          data-testid={`product-card-${product.id}-name`}
        >
          {product.name}
        </div>
        <Badge
          variant="neutral"
          className="mb-2 max-w-full truncate"
          data-testid={`product-card-${product.id}-category`}
        >
          {product.category.name}
        </Badge>
        <div className="mb-1.5" data-testid={`product-card-${product.id}-price`}>
          <div dir="ltr" className="font-display text-saffron text-start text-lg font-semibold">
            {effectivePrice}
          </div>
          {salePricing.isOnSale ? (
            <bdi
              className="text-muted-foreground block text-start text-xs line-through"
              data-testid={`product-card-${product.id}-regular-price`}
            >
              {regularPrice}
            </bdi>
          ) : null}
        </div>
        <div
          className={cn('text-sm', availabilityClass)}
          data-testid={`product-card-${product.id}-inventory`}
        >
          {product.inventory > 0
            ? t('admin.products.card.inventoryLabel', { count: product.inventory })
            : t(availabilityKey)}
        </div>
      </div>

      {/* Footer with kebab (desktop-only affordance) */}
      <div className="flex items-center justify-end px-4 pb-3.5 pt-2">
        <ProductActionsMenu
          product={product}
          storePublished={storePublished === true}
          onRequestDelete={onRequestDelete}
          onRequestPublish={onRequestPublish}
          onRequestUnpublish={onRequestUnpublish}
          testIdPrefix={`product-card-${product.id}`}
          triggerClassName="hidden opacity-0 focus-visible:opacity-100 group-hover:opacity-100 md:flex"
        />
      </div>
    </div>
  );
}
